import { Suspense, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import { Bell, Crown, Gift, Map as MapIcon, Menu, Sprout } from 'lucide-react';
import { PremiumName } from './PremiumName';
import { CardDetailSheet } from '../features/collection/CardDetailSheet';
import { usePageScrollRestoration } from '../hooks/usePageState';
import { formatNumber } from '../services/format';
import type { AchievementDTO } from '@anidex/shared/types';
import { achievementTitle } from '../i18n/catalog';
import { useAchievements, useConfig, useMarkNotificationsRead, useSession, useSocialSummary } from '../services/queries';
import { useToasts } from './Toasts';
import { useRealtime } from '../services/realtime';
import { activeNavigation, HOME_NAV, mobileSection, NAV_GROUPS, navigationBadgeCount, type NavigationIcon, type NavigationItem } from '../services/navigation';
import { Avatar } from './Avatar';
import { ProgressBar } from './Badges';
import { AnimaAmount, LogoPlate } from './Brand';
import { SiteFooter } from './SiteFooter';
import {
  BookIcon,
  BoosterIcon,
  CalendarIcon,
  DiscordIcon,
  CollectionIcon,
  AnimeIcon,
  HomeIcon,
  MarketIcon,
  MessageIcon,
  ProfileIcon,
  TrophyIcon,
  TrashIcon,
  SocialIcon,
  SparkIcon,
  TradeIcon,
} from './Icons';
import { NotificationBell } from './NotificationBell';
import { InstallBanner } from './InstallBanner';
import { PageLoader } from './States';
import { playUiTap } from '../services/ui-sound';
import { LanguageToggle } from './LanguageToggle';
import { SoundToggle } from './SoundToggle';
import { ThemeToggle } from './ThemeToggle';
import { VerificationBanner } from './VerificationBanner';
import { Sheet } from './Sheet';

const NAV_ICONS: Record<NavigationIcon, ComponentType<{ size?: number }>> = {
  home: HomeIcon, booster: BoosterIcon, collection: CollectionIcon, anime: AnimeIcon,
  fragment: SparkIcon, purge: TrashIcon, market: MarketIcon, trade: TradeIcon,
  quest: TrophyIcon, calendar: CalendarIcon, harvest: Sprout, social: SocialIcon,
  atlas: MapIcon, referral: Gift, message: MessageIcon, profile: ProfileIcon,
  notification: Bell, rules: BookIcon, premium: Crown,
};

const MOBILE_NAV = ['/', '/collection', '/boosters', '/marche'].flatMap((to) =>
  [HOME_NAV, ...NAV_GROUPS.flatMap((group) => group.items)].filter((item) => item.to === to),
);

function ClaimBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span className="count-badge">
      {count > 99 ? '99+' : count}
      <span className="visually-hidden"> {label}</span>
    </span>
  );
}

function routeShowsNotifications(pathname: string): boolean {
  if (pathname === '/') return true;
  return ['/quetes', '/calendrier', '/amis', '/messages', '/boosters', '/marche', '/echanges', '/moisson'].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function routeHidesFooter(pathname: string): boolean {
  return pathname === '/messages' || pathname.startsWith('/messages/');
}

function badgeLabel(item: NavigationItem, t: (key: string) => string) {
  if (item.badge === 'home' || item.badge === 'achievements' || item.badge === 'login') return t('nav.badgeClaim');
  if (item.badge === 'messages' || item.icon === 'notification') return t('nav.badgeUnread');
  if (item.badge === 'trades' || item.icon === 'market') return t('nav.badgeTrades');
  return t('nav.badgePending');
}

function useAchievementAlerts(board: AchievementDTO[] | undefined, pathname: string) {
  const { t } = useTranslation();
  const toasts = useToasts();
  const seen = useRef<Map<string, number> | null>(null);
  useEffect(() => {
    if (!board) return;
    const previous = seen.current;
    seen.current = new Map(board.map((achievement) => [achievement.id, achievement.claimable]));
    if (!previous || pathname.startsWith('/boosters') || pathname.startsWith('/quetes')) return;
    for (const achievement of board) {
      if (achievement.claimable <= (previous.get(achievement.id) ?? 0)) continue;
      toasts.push({
        tone: 'reward',
        title: t('toasts.achievementReady'),
        description: t('toasts.achievementReadyDetail', {
          title: achievementTitle(achievement.id),
          tier: achievement.tier + achievement.claimable,
        }),
      });
    }
  }, [board]);
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const session = useSession();
  const user = session.data?.user;
  const config = useConfig().data;
  const summary = useSocialSummary(Boolean(user));
  const markSeen = useMarkNotificationsRead();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const active = activeNavigation(pathname);
  const section = mobileSection(pathname);
  const main = useRef<HTMLElement>(null);
  usePageScrollRestoration(main, 'shell:scroll');
  const level = user?.level;
  useRealtime();
  const achievementBoard = useAchievements(Boolean(user)).data?.achievements;
  const readyAchievements = useMemo(
    () => achievementBoard?.reduce((sum, achievement) => sum + achievement.claimable, 0) ?? 0,
    [achievementBoard],
  );
  useAchievementAlerts(achievementBoard, pathname);
  useEffect(() => setMenuOpen(false), [pathname]);

  const unread = summary.data?.unreadNotifications ?? 0;
  useEffect(() => {
    if (!user || unread <= 0 || !routeShowsNotifications(pathname)) return;
    markSeen.mutate({ path: pathname });
  }, [pathname, user?.id, unread]);
  
  const navigationLink = (item: NavigationItem, variant: 'rail' | 'menu') => {
    const Icon = NAV_ICONS[item.icon];
    const count = navigationBadgeCount(item, readyAchievements, summary.data);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`${variant === 'rail' ? 'rail-nav__link' : 'menu-nav__link'}${item.to === '/boosters' ? ' rail-nav__link--booster' : ''}`}
        aria-current={active?.to === item.to ? 'page' : undefined}
        onClick={() => {
          playUiTap();
          setMenuOpen(false);
        }}
      >
        <span className={`${variant}-nav__icon`} aria-hidden="true"><Icon size={20} /></span>
        <span>{t(item.labelKey)}</span>
        <ClaimBadge count={count} label={badgeLabel(item, t)} />
      </Link>
    );
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#contenu">
        {t('nav.skip')}
      </a>

      <aside className="rail">
        <Link to="/" className="rail__brand" aria-label={t('nav.homeAria')}>
          <LogoPlate size="sm" adaptive />
          <span translate="no">ANIDEX</span>
        </Link>
        <nav className="rail-nav" aria-label={t('nav.primary')}>
          {navigationLink(HOME_NAV, 'rail')}
          {NAV_GROUPS.map((group) => (
            <div key={group.id} className="rail-nav__group">
              <span className="rail-nav__heading">{t(group.labelKey)}</span>
              {group.items.filter((item) => item.rail !== false).map((item) => navigationLink(item, 'rail'))}
            </div>
          ))}
        </nav>
        {user && level && (
          <Link to="/profil" className="rail__player" style={{ ['--avatar-gap' as string]: 'var(--sunken)' }}>
            <Avatar user={user} size={40} />
            <span className="rail__player-tag">
              <span className="rail__player-name"><PremiumName user={user} /></span>
              <span className="rail__player-level">{t('nav.level', { level: level.level })}</span>
            </span>
            <span className="rail__player-xp">
              <ProgressBar value={level.xp - level.currentLevelXp} max={(level.nextLevelXp ?? level.xp) - level.currentLevelXp || 1} label={t('nav.xp')} thin />
              <span>
                {level.nextLevelXp === null
                  ? t('nav.maxLevel')
                  : `${formatNumber(level.xp - level.currentLevelXp)} / ${formatNumber(level.nextLevelXp - level.currentLevelXp)} EXP`}
              </span>
            </span>
          </Link>
        )}
      </aside>

      <header className="hud">
        <Link to="/" className="hud__brand" aria-label={t('nav.homeAria')}>
          <LogoPlate size="sm" adaptive />
          <span translate="no">ANIDEX</span>
        </Link>
        <span className="hud__location">{t(active?.labelKey ?? 'nav.menu')}</span>
        <div className="hud__end">
          <div className="hud__desktop-tools">
            <LanguageToggle />
            <SoundToggle compact />
            <ThemeToggle compact />
            <a className="icon-button hud__discord" href="https://discord.gg/E56FvJArpN" target="_blank" rel="noopener noreferrer" aria-label={t('nav.discord')}>
              <DiscordIcon size={20} />
            </a>
          </div>
          {user && <Link to="/profil" className="hud__anima" title={`${formatNumber(user.anima)} Anima`} aria-label={t('nav.animaValue', { amount: formatNumber(user.anima) })}><AnimaAmount value={user.anima} size={20} animate compact /></Link>}
          <NotificationBell />
          {user && level && (
            <Link to="/profil" className="hud__profile" aria-label={t('nav.profileAria', { name: user.username, level: level.level })}>
              <Avatar user={user} size={36} />
            </Link>
          )}
        </div>
        {user && <InstallBanner />}
      </header>

      <main ref={main} id="contenu" className={`shell__main${pathname.startsWith('/messages/') ? ' shell__main--conversation' : ''}`} tabIndex={-1}>
        {user && config?.emailVerificationRequired && !user.emailVerified && <VerificationBanner />}
        <Suspense fallback={<PageLoader />}>{children ?? <Outlet />}</Suspense>
        {!routeHidesFooter(pathname) && <SiteFooter />}
      </main>

      <nav className="tabbar" aria-label={t('nav.primary')}>
        {MOBILE_NAV.map((item) => {
          const Icon = NAV_ICONS[item.icon];
          const booster = item.to === '/boosters';
          const count = item.to === '/marche' ? summary.data?.pendingTrades ?? 0 : navigationBadgeCount(item, readyAchievements, summary.data);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`tabbar__link${booster ? ' tabbar__link--booster' : ''}`}
              aria-current={section === mobileSection(item.to) ? 'page' : undefined}
              onClick={() => playUiTap()}
            >
              <span className="tabbar__icon">
                <Icon size={booster ? 28 : 24} />
              </span>
              <span>{t(item.labelKey)}</span>
              {count > 0 && (
                <span className="tabbar__badge">
                  <ClaimBadge count={count} label={badgeLabel(item, t)} />
                </span>
              )}
            </Link>
          );
        })}
        <button
          type="button"
          className={`tabbar__link${section === 'more' || menuOpen ? ' is-active' : ''}`}
          aria-label={t('nav.openMenu')}
          aria-expanded={menuOpen}
          aria-haspopup="dialog"
          aria-controls="navigation-menu"
          onClick={() => { playUiTap(); setMenuOpen(true); }}
        >
          <span className="tabbar__icon" aria-hidden="true"><Menu size={24} /></span>
          <span>{t('nav.more')}</span>
          {(summary.data?.pendingFriendRequests ?? 0) + (summary.data?.unreadConversations ?? 0) > 0 && (
            <span className="tabbar__badge"><ClaimBadge count={(summary.data?.pendingFriendRequests ?? 0) + (summary.data?.unreadConversations ?? 0)} label={t('nav.badgePending')} /></span>
          )}
        </button>
      </nav>

      <Sheet id="navigation-menu" className="sheet--navigation" open={menuOpen} onClose={() => setMenuOpen(false)} title={t('nav.menu')} showTitle>
        <nav className="menu-nav" aria-label={t('nav.primary')}>
          {navigationLink(HOME_NAV, 'menu')}
          {NAV_GROUPS.map((group) => (
            <section key={group.id} className="menu-nav__group" aria-labelledby={`menu-${group.id}`}>
              <h3 id={`menu-${group.id}`}>{t(group.labelKey)}</h3>
              <div className="menu-nav__items">{group.items.map((item) => navigationLink(item, 'menu'))}</div>
            </section>
          ))}
          {session.data?.staff && <Link to="/admin" className="button" onClick={() => setMenuOpen(false)}>{t('nav.admin')}</Link>}
        </nav>
        <div className="menu-settings">
          <div><span>{t('language.label')}</span><LanguageToggle /></div>
          <div><span>{t('theme.label')}</span><ThemeToggle compact /></div>
          <div><span>{t('sound.group')}</span><SoundToggle compact /></div>
        </div>
        <SiteFooter />
      </Sheet>
      <CardDetailSheet />
    </div>
  );
}
